import { useMemo } from 'react';
import { useAppState, useBrandId } from 'AppProvider';
import { useSearchParamsObject } from 'utils/url';
import { useLandingPageRouteParams } from 'hooks/useLandingPageRouteParams';
import { ANALYTICS_EVENT, ANALYTICS_PROPERTY, ProductLine } from '@kouto/types';
import { getAnalyticsItemsFromLandingPage } from 'features/analytics';
import { usePageViewEvent } from 'hooks/usePageViewEvent';
import { useListingsV2Params } from 'utils/listingsHelpers';
import { useTranslation } from 'react-i18next';
import { LANDING_PAGE_TITLE_MAP } from 'features/LandingPage/consts';
import { NormalizedPublicListing } from 'types/listingV3';
import useBrandCategories from 'api/categories/useBrandCategories';
import useBrandVibes from 'api/vibes/useBrandVibes';

interface UseAnalyticsViewProps {
  listings?: NormalizedPublicListing[];
  isReadyToTrackAnalytics: boolean;
}

type ListingAnalyticItem = {
  ids: string[];
  titles: string[];
  categories: string[];
};

const eventsMapper: Record<
  ProductLine,
  { event: ANALYTICS_EVENT; pageType: string }
> = {
  [ProductLine.ACTIVATE]: {
    event: ANALYTICS_EVENT.VIEW_EVENTS,
    pageType: 'Activate Landing Page',
  },
  [ProductLine.HOST]: {
    event: ANALYTICS_EVENT.VIEW_HOSTS,
    pageType: 'Host Landing Page',
  },
  [ProductLine.RESERVE]: {
    event: ANALYTICS_EVENT.VIEW_ACTIVITIES,
    pageType: 'Reserve Landing Page',
  },
};

/* Hook to track view analytics for landing page */
export const useLandingPageAnalyticsView = ({
  listings,
  isReadyToTrackAnalytics,
}: UseAnalyticsViewProps) => {
  const { t: translate } = useTranslation();
  const { brand } = useAppState();
  const brandId = useBrandId();
  const { product } = useLandingPageRouteParams();
  const { categoryIds: sanitizedCategoryIds, vibeIds: sanitizedVibeIds } =
    useListingsV2Params();
  const { from, to } = useSearchParamsObject();
  const { categories: brandCategories } = useBrandCategories(brandId, product);
  const { vibes: brandVibes } = useBrandVibes();

  const analyticsItems = useMemo(() => {
    const items = getAnalyticsItemsFromLandingPage({
      listings,
      product,
    });

    const data = items.reduce<ListingAnalyticItem>(
      (acc, item) => {
        acc.ids.push(item.id ?? '');
        acc.titles.push(item.title ?? '');

        acc.categories.push(item.category ?? '');
        return acc;
      },
      {
        ids: [],
        titles: [],
        categories: [],
      },
    );

    return { items, data };
  }, [listings, product]);

  const filterOptionsForAnalytics = useMemo(() => {
    const hasCategoryFilter = sanitizedCategoryIds?.length && brandCategories;
    const hasVibeFilter = sanitizedVibeIds?.length && brandVibes;
    return {
      ...(hasCategoryFilter && {
        categories: sanitizedCategoryIds
          .map((id) => brandCategories.find((c) => c.id === id)?.name)
          .join(', '),
      }),
      ...(hasVibeFilter && {
        vibes: sanitizedVibeIds
          .map((id) => brandVibes.find((c) => c.id === id)?.name)
          .join(', '),
      }),
      ...(from && to && { dateFilter: `${from}--${to}` }),
    };
  }, [
    sanitizedCategoryIds,
    brandCategories,
    sanitizedVibeIds,
    brandVibes,
    from,
    to,
  ]);

  const pageTitle = translate(
    product ? LANDING_PAGE_TITLE_MAP[product] : 'hostProductPageTitle',
  );

  usePageViewEvent({
    eventName: product
      ? eventsMapper[product].event
      : ANALYTICS_EVENT.VIEW_HOME,
    payload: {
      [ANALYTICS_PROPERTY.ListName]: 'All Products',
      [ANALYTICS_PROPERTY.AllExperiences]:
        analyticsItems.data.titles.join(';') || '',
      [ANALYTICS_PROPERTY.AllCategories]:
        analyticsItems.data.categories.join(';') || '',
      [ANALYTICS_PROPERTY.ExperienceCount]:
        analyticsItems.items?.length?.toString(),
      [ANALYTICS_PROPERTY.Products]: JSON.stringify(analyticsItems.items),
      [ANALYTICS_PROPERTY.PageName]: `${pageTitle} | ${brand?.settings?.name}`,
      [ANALYTICS_PROPERTY.PageType]: product
        ? eventsMapper[product].pageType
        : 'Home Page',
      ...(Object.keys(filterOptionsForAnalytics).length > 0 && {
        [ANALYTICS_PROPERTY.FilterOptions]: JSON.stringify(
          filterOptionsForAnalytics,
        ),
      }),
    },
    isNotReady: !isReadyToTrackAnalytics || !brand?.settings?.name,
    key: `page-view-${JSON.stringify(filterOptionsForAnalytics)}-${
      brand?.settings?.name
    }`,
    // This property sendWithPartialPayload was added as an exception for tracking landing_page_view event when the payload is not populated or partially populated.
    // Since we would need to track this landing_page_view event everytime user interacts with page fliters,
    // the payload information like experience_count, experience_titles could potentially not be available based on selected filters.
    // But we would still want event to be tracked in that case.
    sendWithPartialPayload: true,
  });
};
